import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, User, Mail, KeyRound, Save, MessageSquare } from "lucide-react";

function Section({ icon, title, description, children }) {
  return (
    <section className="paper-card p-6 sm:p-7">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-sage-50 text-sage-700 shrink-0">
          {icon}
        </span>
        <div className="flex-1">
          <h2 className="font-heading font-semibold text-sage-900 text-lg">{title}</h2>
          <p className="text-sm text-stone2-600 mt-1">{description}</p>
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Input({ label, testId, ...rest }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input
        data-testid={testId}
        {...rest}
        className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none"
      />
    </div>
  );
}

export default function AccountSettings() {
  const { user, refresh } = useAuth();

  // Name
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  // Email
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  // Password
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  // SMS alerts
  const [smsOptIn, setSmsOptIn] = useState(!!user?.sms_opt_in);
  const [phone, setPhone] = useState(user?.phone_number || "");
  const [savingSms, setSavingSms] = useState(false);

  const saveSms = async (e) => {
    e.preventDefault();
    setSavingSms(true);
    try {
      await api.patch("/auth/me/sms", { sms_opt_in: smsOptIn, phone_number: phone.trim() });
      await refresh();
      toast.success("SMS preferences saved");
    } catch (err) { toast.error(extractError(err)); }
    finally { setSavingSms(false); }
  };

  if (!user) return null;

  const saveName = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return toast.error("Name cannot be blank");
    if (trimmed === user.name) return toast.info("No change to save");
    setSavingName(true);
    try {
      await api.patch("/auth/me", { name: trimmed });
      await refresh();
      toast.success("Name updated");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSavingName(false);
    }
  };

  const saveEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return toast.error("Enter your new email");
    if (!emailPassword) return toast.error("Enter your current password to confirm");
    setSavingEmail(true);
    try {
      await api.post("/auth/me/email", {
        new_email: newEmail.trim().toLowerCase(),
        current_password: emailPassword,
      });
      await refresh();
      toast.success("Email updated");
      setNewEmail("");
      setEmailPassword("");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSavingEmail(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) return toast.error("Enter your current password");
    if (newPassword.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPassword !== confirmPassword) return toast.error("Passwords don't match");
    setSavingPassword(true);
    try {
      await api.post("/auth/me/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="account-settings-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <span className="section-eyebrow">Account settings</span>
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900">Your personal info</h1>
        <p className="text-stone2-600 mt-1 text-sm">
          Update your name, email address, and password. Changes take effect immediately.
        </p>

        <div className="mt-8 space-y-5">
          {/* NAME */}
          <Section
            icon={<User className="w-5 h-5" />}
            title="Name"
            description="How you appear across bookings, messages, and reviews."
          >
            <form onSubmit={saveName} className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <Input
                label="Full name"
                testId="account-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                maxLength={120}
              />
              <button
                type="submit"
                disabled={savingName}
                data-testid="account-name-save"
                className="btn-primary"
              >
                {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save name
              </button>
            </form>
          </Section>

          {/* EMAIL */}
          <Section
            icon={<Mail className="w-5 h-5" />}
            title="Email address"
            description={
              <>
                Current: <span className="font-semibold text-sage-900">{user.email}</span>. Your password
                is required to change it.
              </>
            }
          >
            <form onSubmit={saveEmail} className="space-y-3">
              <Input
                label="New email"
                testId="account-email-input"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
              <Input
                label="Current password"
                testId="account-email-password"
                type="password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                autoComplete="current-password"
              />
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={savingEmail}
                  data-testid="account-email-save"
                  className="btn-primary"
                >
                  {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Update email
                </button>
              </div>
            </form>
          </Section>

          {/* PASSWORD */}
          <Section
            icon={<KeyRound className="w-5 h-5" />}
            title="Password"
            description="Use at least 8 characters with a mix of letters and numbers."
          >            <form onSubmit={savePassword} className="space-y-3">
              <Input
                label="Current password"
                testId="account-current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  label="New password"
                  testId="account-new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                />
                <Input
                  label="Confirm new password"
                  testId="account-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={savingPassword}
                  data-testid="account-password-save"
                  className="btn-primary"
                >
                  {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Update password
                </button>
              </div>
            </form>
          </Section>

          {/* SMS ALERTS */}
          <Section
            icon={<MessageSquare className="w-5 h-5" />}
            title="Text alerts (SMS)"
            description="Get a text when you receive a new message, booking, or SOS. Standard carrier rates may apply."
          >
            <form onSubmit={saveSms} className="space-y-3">
              <Input
                label="Mobile phone (with country code)"
                testId="account-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+14045551234"
                autoComplete="tel"
              />
              <label className="flex items-start gap-3 p-3 rounded-lg border border-stone2-200 bg-white cursor-pointer" data-testid="account-sms-optin-label">
                <input type="checkbox" checked={smsOptIn}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                  data-testid="account-sms-optin" className="mt-0.5 rounded text-sage-600" />
                <span className="text-xs text-stone2-700 leading-relaxed">
                  <strong className="text-sage-900 text-sm block mb-1">
                    Yes, text me transactional alerts
                  </strong>
                  I agree to receive SMS from AutismBabysitters.com about new messages,
                  booking requests, booking status, and account alerts (e.g. password reset).
                  Message frequency varies. Message &amp; data rates may apply.
                  Reply <strong>STOP</strong> to unsubscribe at any time. Reply <strong>HELP</strong> for help.
                  See our{" "}
                  <a href="/privacy" className="text-sage-700 underline hover:text-sage-900" target="_blank" rel="noreferrer">Privacy Policy</a>
                  {" "}and{" "}
                  <a href="/terms" className="text-sage-700 underline hover:text-sage-900" target="_blank" rel="noreferrer">Terms</a>.
                </span>
              </label>
              <div className="flex justify-end">
                <button type="submit" disabled={savingSms} data-testid="account-sms-save" className="btn-primary">
                  {savingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save SMS preferences
                </button>
              </div>
            </form>
          </Section>
        </div>
      </div>
    </div>
  );
}
